/**
 * The study directory: twelve study cards to a page with area, phase and sort controls that
 * cycle when selected (no typing needed in VR), page arrows, and an Enter button for the
 * chosen study. Built for 20–50 studies; the model is the pure `directory.ts`. The same class
 * stands at the reception after onboarding and, larger, beside the Study Gateway; both share
 * the scene's query and selection, so they always agree.
 *
 * `StudyDetail` is the panel to the gateway's right: the chosen study in full.
 */
import type { Manifest, RoomSummary } from '@clinspace/shared';
import { CanvasPanel, type Region } from '../../dashboards/CanvasPanel.ts';
import {
  PALETTE,
  fillRoundRect,
  glassPanel,
  text,
  wrapText,
  font,
} from '../../dashboards/charts.ts';
import type { InteractiveSystem } from '../../ui/Interactive.ts';
import { TOKENS, luminous } from '../../ui/theme.ts';
import {
  SORTS,
  SORT_LABELS,
  directoryPage,
  nextOption,
  type DirectoryEntry,
  type DirectoryQuery,
  type DirectoryStudy,
} from './directory.ts';

const PX_W = 1280;
const PX_H = 832;

export interface DirectoryHost {
  query(): DirectoryQuery;
  setQuery(q: DirectoryQuery): void;
  selected(): string | undefined;
  select(code: string): void;
  enter(code: string): void;
}

export function manifestStudies(manifest: Manifest): DirectoryStudy[] {
  return manifest.studies.map((s) => ({
    code: s.code,
    title: s.title,
    shortTitle: s.shortTitle,
    phase: s.phase,
    therapeuticArea: s.therapeuticArea,
    accentColour: s.accentColour,
    siteCount: s.siteCount,
    subjectCount: s.subjectCount,
    enrolled: s.enrolled,
    target: s.target,
  }));
}

export class StudyDirectory {
  readonly panel: CanvasPanel;
  private rooms: RoomSummary[] = [];
  private readonly studies: DirectoryStudy[];
  /** Codes on the current page, in order (desktop keys 1–9 address them). From the model,
   * not the last draw, so they are right before the panel has drawn. */
  get pageCodes(): string[] {
    return directoryPage(this.studies, this.rooms, this.host.query()).entries.map(
      (e) => e.study.code,
    );
  }

  constructor(
    interactive: InteractiveSystem,
    manifest: Manifest,
    private readonly host: DirectoryHost,
    size: { width: number; name: string },
  ) {
    this.studies = manifestStudies(manifest);
    this.panel = new CanvasPanel({
      width: size.width,
      height: (size.width * PX_H) / PX_W,
      pixelWidth: PX_W,
      pixelHeight: PX_H,
      name: size.name,
      resolution: 1.5,
      glass: true,
    });
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => this.onSelect(region);
    this.panel.attach(interactive);
  }

  get mesh() {
    return this.panel.mesh;
  }

  setRooms(rooms: RoomSummary[]): void {
    this.rooms = rooms;
    this.panel.invalidate();
  }

  invalidate(): void {
    this.panel.invalidate();
  }

  update(): void {
    this.panel.update();
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    ctx.clearRect(0, 0, PX_W, PX_H);
    glassPanel(ctx, { x: 2, y: 2, w: PX_W - 4, h: PX_H - 4 }, 30);
    const q = this.host.query();
    const page = directoryPage(this.studies, this.rooms, q);
    const selected = this.host.selected();

    // Header: title and count, then the cycling controls on the right.
    text(ctx, 'Studies', 36, 66, { size: 30, weight: 600, display: true });
    ctx.font = font(30, 600, true);
    const tw = ctx.measureText('Studies').width;
    text(
      ctx,
      page.matching === page.total
        ? `${page.total} available`
        : `${page.matching} of ${page.total}`,
      36 + tw + 18,
      64,
      { size: 15, colour: PALETTE.muted },
    );
    const controls: { id: string; label: string }[] = [
      { id: 'area', label: `Area: ${q.area}` },
      { id: 'phase', label: `Phase: ${q.phase}` },
      { id: 'sort', label: `Sort: ${SORT_LABELS[q.sort]}` },
    ];
    let cx = PX_W - 36;
    for (const c of [...controls].reverse()) {
      ctx.font = font(14, 600);
      const w = Math.min(300, ctx.measureText(c.label).width + 44);
      cx -= w;
      const r = { x: cx, y: 30, w, h: 46 };
      const hovered = panel.isHovered(c.id);
      const filtered =
        (c.id === 'area' && q.area !== 'All') || (c.id === 'phase' && q.phase !== 'All');
      fillRoundRect(
        ctx,
        r,
        23,
        hovered
          ? 'rgba(63,214,255,0.22)'
          : filtered
            ? 'rgba(63,214,255,0.14)'
            : 'rgba(255,255,255,0.06)',
      );
      ctx.strokeStyle = filtered || hovered ? 'rgba(63,214,255,0.7)' : 'rgba(155,234,255,0.18)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      text(ctx, c.label, r.x + w / 2, r.y + 30, {
        size: 14,
        weight: 600,
        colour: filtered ? TOKENS.cyanSoft : PALETTE.text,
        align: 'center',
        maxWidth: w - 20,
      });
      panel.region({ id: c.id, ...r, tooltip: [c.label, 'Select to change'] });
      cx -= 12;
    }

    // Cards: four columns, three rows.
    const cols = 4;
    const rows = 3;
    const gx = 36;
    const gy = 102;
    const gap = 16;
    const footer = 84;
    const cw = (PX_W - gx * 2 - gap * (cols - 1)) / cols;
    const ch = (PX_H - gy - footer - gap * (rows - 1)) / rows;
    page.entries.forEach((e, i) => {
      const r = {
        x: gx + (i % cols) * (cw + gap),
        y: gy + Math.floor(i / cols) * (ch + gap),
        w: cw,
        h: ch,
      };
      this.drawCard(ctx, panel, e, r, i, e.study.code === selected);
    });
    if (page.entries.length === 0)
      text(ctx, 'No study matches these filters.', PX_W / 2, PX_H / 2, {
        size: 20,
        colour: PALETTE.muted,
        align: 'center',
      });

    // Footer: paging, and Enter for the chosen study.
    const fy = PX_H - footer + 16;
    const arrow = (id: string, label: string, x: number, enabled: boolean): void => {
      const r = { x, y: fy, w: 56, h: 52 };
      const hovered = enabled && panel.isHovered(id);
      fillRoundRect(
        ctx,
        r,
        26,
        hovered
          ? 'rgba(63,214,255,0.22)'
          : enabled
            ? 'rgba(255,255,255,0.07)'
            : 'rgba(255,255,255,0.03)',
      );
      text(ctx, label, x + 28, fy + 35, {
        size: 20,
        weight: 700,
        align: 'center',
        colour: enabled ? PALETTE.text : PALETTE.grid,
      });
      if (enabled)
        panel.region({ id, ...r, tooltip: [id === 'prev' ? 'Previous page' : 'Next page'] });
    };
    arrow('prev', '‹', 36, page.page > 0);
    text(ctx, `Page ${page.page + 1} of ${page.pageCount}`, 36 + 56 + 18, fy + 33, {
      size: 15,
      colour: PALETTE.muted,
    });
    arrow('next', '›', 36 + 56 + 18 + 120, page.page < page.pageCount - 1);
    const chosen = this.studies.find((s) => s.code === selected);
    if (chosen) {
      const label = `Enter ${chosen.code}  ›`;
      ctx.font = font(17, 700, true);
      const w = ctx.measureText(label).width + 60;
      const r = { x: PX_W - 36 - w, y: fy - 2, w, h: 56 };
      const hovered = panel.isHovered('enter');
      const g = ctx.createLinearGradient(r.x, 0, r.x + r.w, 0);
      g.addColorStop(0, hovered ? 'rgba(63,214,255,0.55)' : 'rgba(63,214,255,0.35)');
      g.addColorStop(1, hovered ? 'rgba(61,123,255,0.5)' : 'rgba(61,123,255,0.3)');
      fillRoundRect(ctx, r, 28, g);
      ctx.strokeStyle = 'rgba(155,234,255,0.8)';
      ctx.lineWidth = 2;
      ctx.stroke();
      text(ctx, label, r.x + w / 2, r.y + 36, {
        size: 17,
        weight: 700,
        align: 'center',
        display: true,
      });
      panel.region({
        id: 'enter',
        ...r,
        data: { code: chosen.code },
        tooltip: [`Enter ${chosen.code}`, 'Or walk through the gateway'],
      });
    } else {
      text(ctx, 'Choose a study, then step through the gateway', PX_W - 36, fy + 33, {
        size: 15,
        colour: PALETTE.muted,
        align: 'right',
      });
    }
  }

  private drawCard(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    e: DirectoryEntry,
    r: { x: number; y: number; w: number; h: number },
    index: number,
    selected: boolean,
  ): void {
    const id = `study:${e.study.code}`;
    const hovered = panel.isHovered(id);
    glassPanel(ctx, r, 18, { highlight: selected, hovered, hairline: false });
    // Study colour along the top.
    ctx.fillStyle = luminous(e.study.accentColour);
    ctx.fillRect(r.x + 18, r.y, r.w - 36, 4);
    const x = r.x + 20;
    text(ctx, e.study.code, x, r.y + 44, {
      size: 22,
      weight: 700,
      display: true,
      maxWidth: r.w - 70,
    });
    if (index < 9)
      text(ctx, String(index + 1), r.x + r.w - 20, r.y + 40, {
        size: 13,
        weight: 700,
        colour: PALETTE.muted,
        align: 'right',
      });
    ctx.font = font(14, 600);
    const lines = wrapText(ctx, e.study.shortTitle, r.w - 40).slice(0, 2);
    lines.forEach((l, k) =>
      text(ctx, l, x, r.y + 72 + k * 21, {
        size: 14,
        weight: 600,
        colour: PALETTE.text,
        maxWidth: r.w - 40,
      }),
    );
    text(ctx, `Phase ${e.study.phase} · ${e.study.therapeuticArea}`, x, r.y + 122, {
      size: 12,
      colour: PALETTE.muted,
      maxWidth: r.w - 40,
    });
    // Enrolment bar and numbers.
    const by = r.y + r.h - 46;
    const bw = r.w - 40;
    fillRoundRect(ctx, { x, y: by, w: bw, h: 6 }, 3, 'rgba(255,255,255,0.08)');
    fillRoundRect(ctx, { x, y: by, w: Math.max(6, bw * e.progress), h: 6 }, 3, TOKENS.cyan);
    text(ctx, `${e.enrolled}/${e.target} enrolled`, x, by + 28, {
      size: 12,
      colour: PALETTE.muted,
    });
    const right: string[] = [];
    if (e.openQueries !== undefined) right.push(`${e.openQueries} open`);
    if (e.people) right.push(`${e.people} inside`);
    if (right.length)
      text(ctx, right.join(' · '), r.x + r.w - 20, by + 28, {
        size: 12,
        weight: 600,
        colour: e.people ? TOKENS.cyanSoft : e.openQueries ? TOKENS.amber : PALETTE.muted,
        align: 'right',
      });
    panel.region({
      id,
      ...r,
      data: { code: e.study.code },
      tooltip: [
        e.study.code,
        e.study.shortTitle,
        e.people
          ? `${e.people} ${e.people === 1 ? 'person' : 'people'} inside. Join with your team's room code.`
          : 'Nobody inside yet',
      ],
    });
  }

  private onSelect(region: Region): void {
    const q = this.host.query();
    const data = region.data as { code?: string } | undefined;
    if (region.id.startsWith('study:') && data?.code) this.host.select(data.code);
    else if (region.id === 'enter' && data?.code) this.host.enter(data.code);
    else if (region.id === 'prev') this.host.setQuery({ ...q, page: q.page - 1 });
    else if (region.id === 'next') this.host.setQuery({ ...q, page: q.page + 1 });
    else {
      const page = directoryPage(this.studies, this.rooms, q);
      if (region.id === 'area')
        this.host.setQuery({ ...q, area: nextOption(page.areas, q.area), page: 0 });
      if (region.id === 'phase')
        this.host.setQuery({ ...q, phase: nextOption(page.phases, q.phase), page: 0 });
      if (region.id === 'sort')
        this.host.setQuery({ ...q, sort: nextOption(SORTS, q.sort), page: 0 });
    }
  }

  dispose(interactive: InteractiveSystem): void {
    this.panel.detach(interactive);
    this.panel.dispose();
  }
}

const DETAIL_W = 800;
const DETAIL_H = 880;

/** The chosen study in full, beside the gateway. */
export class StudyDetail {
  readonly panel: CanvasPanel;
  private rooms: RoomSummary[] = [];
  private readonly studies: DirectoryStudy[];

  constructor(
    interactive: InteractiveSystem,
    manifest: Manifest,
    private readonly host: DirectoryHost,
    width: number,
  ) {
    this.studies = manifestStudies(manifest);
    this.panel = new CanvasPanel({
      width,
      height: (width * DETAIL_H) / DETAIL_W,
      pixelWidth: DETAIL_W,
      pixelHeight: DETAIL_H,
      name: 'study-detail',
      resolution: 1.5,
      glass: true,
    });
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => {
      const code = (region.data as { code?: string } | undefined)?.code;
      if (region.id === 'enter' && code) this.host.enter(code);
    };
    this.panel.attach(interactive);
  }

  get mesh() {
    return this.panel.mesh;
  }

  setRooms(rooms: RoomSummary[]): void {
    this.rooms = rooms;
    this.panel.invalidate();
  }

  invalidate(): void {
    this.panel.invalidate();
  }

  update(): void {
    this.panel.update();
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    ctx.clearRect(0, 0, DETAIL_W, DETAIL_H);
    glassPanel(ctx, { x: 2, y: 2, w: DETAIL_W - 4, h: DETAIL_H - 4 }, 30);
    const code = this.host.selected();
    const study = this.studies.find((s) => s.code === code);
    const x = 44;
    if (!study) {
      text(ctx, 'STUDY', x, 80, { size: 14, weight: 700, colour: TOKENS.cyanSoft });
      text(ctx, 'Choose a study', x, 140, { size: 34, weight: 600, display: true });
      ctx.font = font(17);
      wrapText(
        ctx,
        'Pick a study on the directory to the left of the gateway. Its colour will fill the gateway, and the path to it will light up.',
        DETAIL_W - 88,
      ).forEach((l, i) => text(ctx, l, x, 200 + i * 30, { size: 17, colour: PALETTE.muted }));
      return;
    }
    const page = directoryPage([study], this.rooms, {
      area: 'All',
      phase: 'All',
      sort: 'portfolio',
      page: 0,
    });
    const e = page.entries[0]!;
    ctx.fillStyle = luminous(study.accentColour);
    ctx.fillRect(30, 2, DETAIL_W - 60, 5);
    text(ctx, `PHASE ${study.phase} · ${study.therapeuticArea.toUpperCase()}`, x, 76, {
      size: 13,
      weight: 700,
      colour: TOKENS.cyanSoft,
      maxWidth: DETAIL_W - 88,
    });
    text(ctx, study.code, x, 138, { size: 44, weight: 700, display: true });
    ctx.font = font(19, 600);
    const lines = wrapText(ctx, study.title, DETAIL_W - 88).slice(0, 4);
    lines.forEach((l, i) => text(ctx, l, x, 190 + i * 32, { size: 19, weight: 600 }));
    let y = 190 + lines.length * 32 + 30;
    // Key numbers.
    const stats: [string, string, string?][] = [
      ['Sites', String(study.siteCount)],
      ['Subjects', String(study.subjectCount)],
      [
        'Open queries',
        e.openQueries === undefined ? '–' : String(e.openQueries),
        e.openQueries ? TOKENS.amber : undefined,
      ],
    ];
    const sw = (DETAIL_W - 88 - 32) / 3;
    stats.forEach(([label, value, colour], i) => {
      const r = { x: x + i * (sw + 16), y, w: sw, h: 100 };
      glassPanel(ctx, r, 16, { hairline: false });
      text(ctx, label.toUpperCase(), r.x + 18, r.y + 32, {
        size: 11,
        weight: 700,
        colour: PALETTE.muted,
      });
      text(ctx, value, r.x + 18, r.y + 78, {
        size: 30,
        weight: 700,
        display: true,
        colour: colour ?? PALETTE.text,
      });
    });
    y += 130;
    text(ctx, 'ENROLMENT', x, y, { size: 12, weight: 700, colour: PALETTE.muted });
    text(ctx, `${e.enrolled} of ${e.target}`, DETAIL_W - 44, y, {
      size: 15,
      weight: 600,
      align: 'right',
    });
    fillRoundRect(ctx, { x, y: y + 16, w: DETAIL_W - 88, h: 10 }, 5, 'rgba(255,255,255,0.08)');
    fillRoundRect(
      ctx,
      { x, y: y + 16, w: Math.max(10, (DETAIL_W - 88) * e.progress), h: 10 },
      5,
      TOKENS.cyan,
    );
    y += 70;
    text(ctx, 'INSIDE NOW', x, y, { size: 12, weight: 700, colour: PALETTE.muted });
    // Who is inside is never shown: people meet only by sharing a room code (2026-10-07).
    ctx.font = font(16);
    const insideLines = e.people
      ? [
          `${e.people} ${e.people === 1 ? 'person' : 'people'} in this study's rooms`,
          "Join them with your team's room code.",
        ]
      : ['Nobody yet'];
    insideLines.forEach((l, i) =>
      text(ctx, l, x, y + 32 + i * 27, {
        size: 16,
        colour: e.people && i === 0 ? PALETTE.text : PALETTE.muted,
        maxWidth: DETAIL_W - 88,
      }),
    );
    // Enter.
    const r = { x, y: DETAIL_H - 120, w: DETAIL_W - 88, h: 76 };
    const hovered = panel.isHovered('enter');
    const g = ctx.createLinearGradient(r.x, 0, r.x + r.w, 0);
    g.addColorStop(0, hovered ? 'rgba(63,214,255,0.55)' : 'rgba(63,214,255,0.35)');
    g.addColorStop(1, hovered ? 'rgba(61,123,255,0.5)' : 'rgba(61,123,255,0.3)');
    fillRoundRect(ctx, r, 38, g);
    ctx.strokeStyle = 'rgba(155,234,255,0.8)';
    ctx.lineWidth = 2;
    ctx.stroke();
    text(ctx, `Enter ${study.code}  ›`, r.x + r.w / 2, r.y + 48, {
      size: 22,
      weight: 700,
      align: 'center',
      display: true,
    });
    panel.region({
      id: 'enter',
      ...r,
      data: { code: study.code },
      tooltip: [`Enter ${study.code}`, 'Or walk through the gateway'],
    });
  }

  dispose(interactive: InteractiveSystem): void {
    this.panel.detach(interactive);
    this.panel.dispose();
  }
}
